import classNames from 'classnames/bind';
import Button from '../../../components/Button';
import Card, { CardHeader } from '../../../components/Card';
import CardButton from '../../../components/CardButton';
import Icon from '../../../components/Icon/Icon';
import { IconName } from '../../../components/Icon/utils/types';
import {
  createEnrollment,
  continueEnrollment,
  setIsDFINAdmin,
  getLatestEnrollment,
} from '../../../state/enrollment/enrollmentActions';
import styles from './PathSelection.module.scss';
import { NavLink } from 'react-router-dom';
import { useEffect, useState } from 'react';

const cx = classNames.bind(styles);

const enum PathSelectionOption {
  LET_DFIN_ASSIST = 'letDFINAssist',
  I_WILL_ADMIN = 'iWillAdmin',
  CONTINUE_LAST_ENROLLMENT = 'continueLastEnrollment',
}

export interface PathSelectionProps {
  isDFINAdmin: boolean;
  isLoading: boolean;
  latestEnrollmentId?: string;
  setIsDFINAdmin: typeof setIsDFINAdmin;
  createEnrollment: typeof createEnrollment;
  continueEnrollment: typeof continueEnrollment;
  getLatestEnrollment: typeof getLatestEnrollment;
}

const PathSelection = (props: PathSelectionProps) => {
  const {
    isDFINAdmin,
    isLoading,
    latestEnrollmentId,
    setIsDFINAdmin,
    createEnrollment,
    continueEnrollment,
    getLatestEnrollment,
  } = props;

  const [selectedPath, setSelectedPath] = useState(
    isDFINAdmin
      ? PathSelectionOption.LET_DFIN_ASSIST
      : PathSelectionOption.I_WILL_ADMIN
  );

  const isDFINAdminSelected: boolean =
    selectedPath === PathSelectionOption.LET_DFIN_ASSIST;
  const isIWillAdminSelected: boolean =
    selectedPath === PathSelectionOption.I_WILL_ADMIN;
  const isContinueLastEnrollmentSelected: boolean =
    selectedPath === PathSelectionOption.CONTINUE_LAST_ENROLLMENT;

  const setDFINAdmin = () =>
    setSelectedPath(PathSelectionOption.LET_DFIN_ASSIST);
  const setUserAdmin = () => setSelectedPath(PathSelectionOption.I_WILL_ADMIN);
  const setContinueLastEnrollment = () =>
    setSelectedPath(PathSelectionOption.CONTINUE_LAST_ENROLLMENT);

  const onCreateEnrollmentSuccess = () => setIsDFINAdmin(isDFINAdminSelected);

  const onContinue = () => {
    if (latestEnrollmentId && isContinueLastEnrollmentSelected) {
      continueEnrollment(latestEnrollmentId);
    } else {
      createEnrollment(onCreateEnrollmentSuccess);
    }
  };

  const headerSubtitle = !!latestEnrollmentId
    ? 'Abandon your previous, incomplete enrollment and start a new one or continue from where you saved by choosing Continue.'
    : 'Get started. Select which path best suits your approach to SEC EDGAR filings.';

  useEffect(() => {
    getLatestEnrollment();
  }, []);

  return (
    <Card className={cx('path-selection__card')}>
      <CardHeader
        title="EDGAR Next Enrollment and Administration"
        subtitle={headerSubtitle}
        classes={{
          subtitle: cx('path-selection__subtitle', {
            'path-selection__subtitle--continue': !!latestEnrollmentId,
          }),
        }}
      />
      <div
        className={cx('path-selection__body', {
          'path-selection__body--continue': !!latestEnrollmentId,
        })}
      >
        <div className={cx('path-selection__new-enrollment')}>
          <CardButton
            isActive={isDFINAdminSelected}
            onClick={setDFINAdmin}
            className={cx('path-selection__card-btn')}
          >
            <span className={cx('path-selection__card-btn-title')}>
              Let DFIN Assist
            </span>
            <div className={cx('path-selection__card-btn-body')}>
              <Icon
                name={IconName.Users}
                width={63}
                height={50}
                className={cx('path-selection__card-btn-icon')}
              />
              <div className={cx('path-selection__card-btn-text')}>
                Allow DFIN to become an administrator on your EDGAR Account to
                assist with Filer Agent Delegation to DFIN
              </div>
            </div>
          </CardButton>
          <CardButton
            isActive={isIWillAdminSelected}
            onClick={setUserAdmin}
            className={cx('path-selection__card-btn')}
          >
            <div className={cx('path-selection__card-btn-body')}>
              <Icon
                name={IconName.User}
                width={25}
                height={30}
                className={cx('path-selection__card-btn-icon')}
              />
              <div className={cx('path-selection__card-btn-text')}>
                I will name the administrator roles on my account who will
                assist with Filer Agent Delegation, if applicable.
              </div>
            </div>
          </CardButton>
        </div>
        {latestEnrollmentId && (
          <>
            <div className={cx('path-selection__or-text')}>OR</div>
            <CardButton
              isActive={isContinueLastEnrollmentSelected}
              onClick={setContinueLastEnrollment}
              wrapperClassName={cx('path-selection__card-btn--continue')}
              className={cx('path-selection__card-btn')}
            >
              <span className={cx('path-selection__card-btn-title')}>
                Continue
              </span>
              <div
                className={cx(
                  'path-selection__card-btn-body',
                  'path-selection__card-btn-body--continue'
                )}
              >
                <Icon
                  name={IconName.Continue}
                  width={80}
                  height={80}
                  className={cx('path-selection__card-btn-icon')}
                />
                <div className={cx('path-selection__card-btn-text')}>
                  Continue my previous, incomplete enrollment.
                </div>
              </div>
            </CardButton>
          </>
        )}
      </div>
      <div className={cx('path-selection__footer')}>
        <NavLink
          to="/path-selection-read-more"
          className={cx('path-selection__link-btn')}
        >
          Read more to help with your selection.
        </NavLink>
        <Button onClick={onContinue} isLoading={isLoading}>
          Continue
        </Button>
      </div>
    </Card>
  );
};

export default PathSelection;
